/* global React, Icon, Card, Button, Modal, useState, useEffect, t */
// ============================================================
// KLIENTENS INNGANG TIL DIALOGEN MED REGNSKAPSFØREREN
// ============================================================
// To inngangar med hver sin jobb, og ÉN skuff bak dem begge:
//
//   `FirmChatHomeCard`  — kortet på Hjem. Slik oppdager man at kanalen finnes,
//                         og det er her man ser hva som sist ble sagt. Kortet
//                         eier skuffen og bærer uleste-telleren.
//   `FirmChatLoginModal`— én gang per innlogging når noe ligger ulest. Finnes
//                         fordi byrået trenger en vei fram til klienter som
//                         sjelden logger inn.
//
// Modalen åpner ikke sin egen skuff — den sender et vindusvarsel som kortet
// fanger. To skuffer på samme tråd ville vært to lesemerker på samme samtale,
// og den ene ville overskrevet den andre.
//
// Begge rendres bare når foretaket har et AKTIVT byråoppdrag. Uten oppdrag
// finnes det ingen motpart, og reglene ville avvist lesingen uansett.

const FIRM_CHAT_OPEN_EVENT = 'plumme-open-firm-chat';
const firmChatSeenKey = (businessId) => 'plumme-firm-chat-seen:' + String(businessId || '');

// Det aktive oppdraget for foretaket brukeren står i. Flere byråer på samme
// klient er mulig i modellen, men flaten viser det første: en klient med to
// samtidige byråer er ikke en tilstand produktet har en historie for ennå.
function useActiveEngagement() {
  const read = () => (window.PlummeBackend?.firmEngagements || [])[0] || null;
  const [engagement, setEngagement] = useState(read);
  useEffect(() => {
    const handler = () => setEngagement(read());
    window.addEventListener('plumme-backend-state', handler);
    return () => window.removeEventListener('plumme-backend-state', handler);
  }, []);
  return engagement;
}

// Tråddokumentet er ett lite dokument. Å abonnere på det fra flere komponenter
// er billig — Firestore-klienten deler den underliggende lyttingen på samme sti.
function useFirmChatThread(engagement) {
  const businessId = window.PlummeBackend?.activeBusinessId || '';
  const firmId = engagement?.firmId || '';
  const [thread, setThread] = useState(null);
  useEffect(() => {
    if (!businessId || !firmId) { setThread(null); return undefined; }
    return window.PlummeBackend?.subscribeFirmClientChatThread?.({ businessId, firmId }, setThread);
  }, [businessId, firmId]);
  return { businessId, firmId, thread, unread: window.fcHasUnread(thread, 'client') };
}

function FirmChatHomeCard() {
  const engagement = useActiveEngagement();
  const { businessId, firmId, thread, unread } = useFirmChatThread(engagement);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);

  // Telleren trenger meldingene, men bare når det faktisk ligger noe ulest.
  // Uten den betingelsen ville hver klient med en gammel, lest samtale abonnert
  // på 200 meldinger for å vise tallet null.
  useEffect(() => {
    if (!businessId || !firmId || !unread) { setMessages([]); return undefined; }
    return window.PlummeBackend?.subscribeFirmClientChatMessages?.({ businessId, firmId }, setMessages);
  }, [businessId, firmId, unread]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(FIRM_CHAT_OPEN_EVENT, handler);
    return () => window.removeEventListener(FIRM_CHAT_OPEN_EVENT, handler);
  }, []);

  if (!engagement?.firmId) return null;

  const count = unread ? window.fcUnreadCount(messages, thread, 'client') : 0;
  const last = thread?.lastMessageAt
    ? (thread.lastMessageSide === 'firm'
      ? t('chat.cardFromFirm', 'Regnskapsføreren skrev')
      : t('chat.cardFromYou', 'Du skrev')) + ' ' + window.fcTime(thread.lastMessageAt)
    : t('chat.cardNone', 'Ingen meldinger ennå');

  return <React.Fragment>
    <Card className="chatd-home">
      <div className="chatd-home-card">
        <span className="chatd-home-ico"><Icon name="building" size={20} /></span>
        <div className="chatd-home-body">
          <b className="t-h3" style={{ fontSize: 15 }}>
            {engagement.firmName || t('chat.firm', 'Regnskapsføreren din')}
            {count > 0 ? <span className="chatd-home-count">{count}</span> : null}
          </b>
          <div className="t-sm muted chatd-home-last" style={{ fontWeight: 600, marginTop: 4 }}>
            {last}{thread?.lastMessageText ? ' · ' + thread.lastMessageText : ''}
          </div>
        </div>
        <Button
          size="sm"
          variant={unread ? 'primary' : 'secondary'}
          onClick={() => setOpen(true)}
          data-help-title={t('chat.helpTitle', 'Regnskapsføreren din')}
          data-help={t('chat.help', 'Skriv direkte til regnskapsføreren din. Bare du som eier eller administrator ser denne samtalen.')}
        >
          {unread ? t('chat.cardOpenUnread', 'Les meldingen') : t('chat.cardOpen', 'Send en melding')}
        </Button>
      </div>
    </Card>
    {open && window.FirmClientChat ? <window.FirmClientChat
      businessId={businessId}
      firmId={firmId}
      firmName={engagement.firmName}
      side="client"
      onClose={() => setOpen(false)}
    /> : null}
  </React.Fragment>;
}

// Én gang per innlogging, ikke én gang per sidevisning. Uten `sessionStorage`
// ville modalen dukket opp igjen ved hvert rutebytte, og en påminnelse man må
// lukke ti ganger om dagen er ikke en påminnelse — den er en irritasjon man
// lærer seg å klikke bort uten å lese.
function FirmChatLoginModal() {
  const engagement = useActiveEngagement();
  const { businessId, thread, unread } = useFirmChatThread(engagement);
  const [dismissed, setDismissed] = useState(false);

  const seenKey = firmChatSeenKey(businessId);
  const alreadySeen = () => {
    try { return window.sessionStorage.getItem(seenKey) === String(thread?.lastMessageAt || ''); } catch { return false; }
  };
  const remember = () => {
    try { window.sessionStorage.setItem(seenKey, String(thread?.lastMessageAt || '')); } catch { /* privat modus */ }
  };

  if (!engagement?.firmId || !unread || dismissed || alreadySeen()) return null;

  const close = () => { remember(); setDismissed(true); };
  const openChat = () => { close(); window.dispatchEvent(new CustomEvent(FIRM_CHAT_OPEN_EVENT)); };

  return <Modal
    open
    onClose={close}
    max={440}
    title={t('chat.modalTitle', 'Du har en ulest melding')}
    footer={<React.Fragment>
      <Button variant="secondary" onClick={close}>{t('app.close', 'Lukk')}</Button>
      <Button onClick={openChat}>{t('chat.modalOpen', 'Åpne chat')}</Button>
    </React.Fragment>}
  >
    <div className="t-sm" style={{ fontWeight: 600, lineHeight: 1.5 }}>
      {(engagement.firmName || t('chat.firm', 'Regnskapsføreren din'))
        + ' ' + t('chat.modalBody', 'har sendt deg en melding du ikke har lest.')}
    </div>
    {thread?.lastMessageText
      ? <div className="t-sm muted" style={{ fontWeight: 600, marginTop: 10, fontStyle: 'italic' }}>
        «{thread.lastMessageText}»
      </div>
      : null}
  </Modal>;
}

Object.assign(window, { FirmChatHomeCard, FirmChatLoginModal, FIRM_CHAT_OPEN_EVENT });
